import { ExtractComponentProp, Reference } from '@approvalmax/types';
import { Form } from '@approvalmax/ui/src/components';
import { mathService, numberHelpers } from '@approvalmax/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import { actions } from 'modules/common';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { getCustomFieldsColumnDefinition } from 'pages/requestForm/containers/netsuite/components/NetSuiteLineCustomField/NetSuiteLineCustomField.config';
import { customRecordFieldNames } from 'pages/requestForm/containers/netsuite/components/NetSuiteLineCustomField/NetSuiteLineCustomField.constants';
import { useAvailableCustomFields } from 'pages/requestForm/containers/netsuite/components/NetSuiteLineCustomField/NetSuiteLineCustomField.hooks';
import {
    CustomFieldValueKey,
    NetSuiteExpenseLineExtendedExpenseReport,
} from 'pages/requestForm/containers/netsuite/data/types';
import { setCurrencyPrecision } from 'pages/requestForm/containers/netsuite/helpers';
import { useCurrencyPrecision, useGroupEditItems } from 'pages/requestForm/containers/netsuite/hooks';
import { useExchangeRates } from 'pages/requestForm/containers/netsuite/hooks/useExchangeRates/useExchangeRates';
import { getLineCurrencyPrecision } from 'pages/requestForm/containers/netsuite/NetSuiteExpenseReportRequestForm/helpers';
import {
    useExpenseLinesUploadAttachments,
    useExpenseReportExpenseLines,
} from 'pages/requestForm/containers/netsuite/NetSuiteExpenseReportRequestForm/hooks';
import {
    netSuiteAttachmentUploadingLineIdsState,
    netSuiteExpenseReportFieldValuesState,
} from 'pages/requestForm/containers/netsuite/NetSuiteExpenseReportRequestForm/NetSuiteExpenseReportRequestForm.states';
import { getFileNameWithRequestId } from 'pages/requestForm/containers/netsuite/NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { companyIdState, netSuiteShowErrorsState } from 'pages/requestForm/containers/netsuite/resources/states';
import { NetSuiteExpenseCategory, NetSuiteSalesTaxItem } from 'pages/requestForm/data/netsuite/NetSuiteContext';
import { getNetSuiteContext } from 'pages/requestForm/selectors/netsuite/netSuiteSelectors';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { RequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { getRequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
    useNetSuiteExpenseLinesPreviewAttachment,
    useNetSuiteExpenseReportExpenseLinesFieldSettings,
} from 'shared/hooks';
import { netSuiteExpenseLinesState } from 'shared/states';

import { getColumnsDefinition } from './ExpenseSection.config';
import { linesNamePrefix } from './ExpenseSection.constants';
import { getGroupEditItemsAvailableFields } from './ExpenseSection.helpers';
import { messages } from './ExpenseSection.messages';
import { createSchema } from './ExpenseSection.schema';
import { ColumnDefinitionForExpenseReportExtended, FormValues, Line } from './ExpenseSection.types';

export const useChangeCategory = () => {
    const fieldValues = useAtomValue(netSuiteExpenseReportFieldValuesState);
    const { accounts } = useSelector(getNetSuiteContext);
    const currencyPrecision = useCurrencyPrecision(fieldValues.currency);
    const { setOneExpenseLine } = useExpenseReportExpenseLines();

    return (li: NetSuiteExpenseLineExtendedExpenseReport, value?: NetSuiteExpenseCategory | Reference | null) => {
        if (!value) {
            setOneExpenseLine(li.id, {
                category: null,
                expenseAccount: null,
            });

            return;
        }

        const accountId = 'accountId' in value ? value.accountId : null;
        const rates = 'rates' in value ? value.rates : [];

        const relatedAccount = accounts.find((account) => account.id === accountId);
        const categoryRate = rates.find(
            (rate) => rate.currency === fieldValues.currency?.text || rate.currency === fieldValues.currency?.id
        );

        const amount = setCurrencyPrecision(categoryRate?.defaultRate, currencyPrecision) || li.amount;
        const rate = (li.taxRate ?? 0) / 100;
        const taxAmount = numberHelpers.isNumber(amount)
            ? setCurrencyPrecision(mathService.multiply(amount, rate ?? 0), currencyPrecision)
            : 0;
        const grossAmount =
            li.taxCode && numberHelpers.isNumber(amount) && numberHelpers.isNumber(taxAmount)
                ? setCurrencyPrecision(amount + taxAmount, currencyPrecision)
                : null;

        setOneExpenseLine(li.id, {
            category: value,
            expenseAccount: relatedAccount || null,
            amount,
            taxAmount,
            grossAmount,
        });
    };
};

export const useChangeForeignAmount = () => {
    const fieldValues = useAtomValue(netSuiteExpenseReportFieldValuesState);
    const { setOneExpenseLine } = useExpenseReportExpenseLines();
    const { currencies } = useSelector(getNetSuiteContext);
    const currencyPrecision = useCurrencyPrecision(fieldValues.currency);

    return (li: NetSuiteExpenseLineExtendedExpenseReport, value: number | null) => {
        if (!numberHelpers.isNumber(value)) {
            setOneExpenseLine(li.id, {
                foreignAmount: null,
                amount: null,
                taxAmount: null,
                grossAmount: null,
            });

            return;
        }

        const lineCurrencyPrecision = getLineCurrencyPrecision(li, currencies);

        const foreignAmount = setCurrencyPrecision(value, lineCurrencyPrecision);
        const amount =
            numberHelpers.isNumber(foreignAmount) && numberHelpers.isNumber(li.exchangeRate)
                ? setCurrencyPrecision(foreignAmount * li.exchangeRate, currencyPrecision)
                : li.amount;
        const taxAmount =
            numberHelpers.isNumber(amount) && li.taxRate
                ? setCurrencyPrecision(mathService.multiply(amount, li.taxRate / 100), currencyPrecision)
                : 0;
        const grossAmount =
            li.taxCode && numberHelpers.isNumber(amount) && numberHelpers.isNumber(taxAmount)
                ? setCurrencyPrecision(amount + taxAmount, currencyPrecision)
                : null;

        setOneExpenseLine(li.id, {
            foreignAmount,
            amount,
            taxAmount,
            grossAmount,
        });
    };
};

export const useChangeCurrency = () => {
    const companyId = useAtomValue(companyIdState);
    const fieldValues = useAtomValue(netSuiteExpenseReportFieldValuesState);
    const { setOneExpenseLine } = useExpenseReportExpenseLines();
    const { currencies } = useSelector(getNetSuiteContext);
    const currencyPrecision = useCurrencyPrecision(fieldValues.currency);

    const { data: exchangeRates } = useExchangeRates(companyId, fieldValues.date, fieldValues.currency?.id, true);

    return (li: NetSuiteExpenseLineExtendedExpenseReport, value?: domain.NetSuiteCurrency | Reference | null) => {
        if (!value) {
            setOneExpenseLine(li.id, {
                currency: value,
                exchangeRate: null,
                amount: null,
                grossAmount: null,
            });

            return;
        }

        let exchangeRate: number | null = null;

        if (exchangeRates) {
            const targetExchangeRate = exchangeRates.rates.find((rate) => rate.id === value.id);

            exchangeRate = targetExchangeRate?.rateValue || null;
        }

        const lineCurrencyPrecision = getLineCurrencyPrecision(li, currencies);

        const amount =
            numberHelpers.isNumber(li.foreignAmount) && numberHelpers.isNumber(exchangeRate)
                ? setCurrencyPrecision(li.foreignAmount * exchangeRate, currencyPrecision)
                : li.amount;
        const taxAmount = numberHelpers.isNumber(li.taxAmount)
            ? setCurrencyPrecision(li.taxAmount, currencyPrecision)
            : 0;
        const grossAmount =
            li.taxCode && numberHelpers.isNumber(amount)
                ? setCurrencyPrecision(amount + (taxAmount ?? 0), currencyPrecision)
                : null;
        const foreignAmount =
            exchangeRate && numberHelpers.isNumber(amount)
                ? setCurrencyPrecision(amount / exchangeRate, lineCurrencyPrecision)
                : li.foreignAmount;

        setOneExpenseLine(li.id, {
            currency: value,
            exchangeRate,
            amount,
            taxAmount,
            grossAmount,
            foreignAmount,
        });
    };
};

export const useChangeExchangeRate = () => {
    const fieldValues = useAtomValue(netSuiteExpenseReportFieldValuesState);
    const { setOneExpenseLine } = useExpenseReportExpenseLines();
    const { currencies } = useSelector(getNetSuiteContext);
    const currencyPrecision = useCurrencyPrecision(fieldValues.currency);

    return (li: NetSuiteExpenseLineExtendedExpenseReport, value: number | null) => {
        if (!numberHelpers.isNumber(value)) {
            setOneExpenseLine(li.id, {
                exchangeRate: value,
                amount: null,
                grossAmount: null,
            });

            return;
        }

        const lineCurrencyPrecision = getLineCurrencyPrecision(li, currencies);
        const amount = numberHelpers.isNumber(li.foreignAmount)
            ? setCurrencyPrecision(li.foreignAmount * value, currencyPrecision)
            : li.amount;
        const taxAmount = numberHelpers.isNumber(li.taxAmount)
            ? setCurrencyPrecision(li.taxAmount, currencyPrecision)
            : 0;
        const grossAmount =
            li.taxCode && numberHelpers.isNumber(amount)
                ? setCurrencyPrecision(amount + (taxAmount ?? 0), currencyPrecision)
                : null;
        const foreignAmount =
            value && numberHelpers.isNumber(amount)
                ? setCurrencyPrecision(amount / value, lineCurrencyPrecision)
                : li.foreignAmount;

        setOneExpenseLine(li.id, {
            exchangeRate: value,
            amount,
            taxAmount,
            grossAmount,
            foreignAmount,
        });
    };
};

export const useChangeAmount = () => {
    const fieldValues = useAtomValue(netSuiteExpenseReportFieldValuesState);
    const { currencies } = useSelector(getNetSuiteContext);
    const { setOneExpenseLine } = useExpenseReportExpenseLines();
    const currencyPrecision = useCurrencyPrecision(fieldValues.currency);

    return (li: NetSuiteExpenseLineExtendedExpenseReport, value: number | null) => {
        const lineCurrencyPrecision = getLineCurrencyPrecision(li, currencies);

        const amount = setCurrencyPrecision(value, currencyPrecision) ?? 0;
        const rate = (li.taxRate ?? 0) / 100;
        const taxAmount = setCurrencyPrecision(mathService.multiply(amount, rate), currencyPrecision);
        const grossAmount = li.taxCode ? setCurrencyPrecision(amount + taxAmount, currencyPrecision) : null;
        const foreignAmount = li.exchangeRate
            ? setCurrencyPrecision(amount / li.exchangeRate, lineCurrencyPrecision)
            : li.foreignAmount;

        setOneExpenseLine(li.id, {
            amount,
            taxAmount,
            grossAmount,
            foreignAmount,
        });
    };
};

export const useChangeTaxCode = () => {
    const fieldValues = useAtomValue(netSuiteExpenseReportFieldValuesState);
    const { currencies } = useSelector(getNetSuiteContext);
    const { setOneExpenseLine } = useExpenseReportExpenseLines();
    const currencyPrecision = useCurrencyPrecision(fieldValues.currency);

    return (li: NetSuiteExpenseLineExtendedExpenseReport, value?: NetSuiteSalesTaxItem | Reference | null) => {
        const lineCurrencyPrecision = getLineCurrencyPrecision(li, currencies);

        const rate = value && 'rate' in value ? (value.rate ?? 0) / 100 : 0;
        const amount = li.amount ?? 0;
        const taxAmount = setCurrencyPrecision(mathService.multiply(amount, rate), currencyPrecision);
        const grossAmount = value ? setCurrencyPrecision(amount + taxAmount, currencyPrecision) : null;
        const foreignAmount = li.exchangeRate
            ? setCurrencyPrecision(amount / li.exchangeRate, lineCurrencyPrecision)
            : li.foreignAmount;

        setOneExpenseLine(li.id, {
            taxCode: value,
            taxRate: rate || null,
            taxAmount,
            grossAmount,
            foreignAmount,
        });
    };
};

export const useChangeGrossAmount = () => {
    const fieldValues = useAtomValue(netSuiteExpenseReportFieldValuesState);
    const { currencies } = useSelector(getNetSuiteContext);
    const { setOneExpenseLine } = useExpenseReportExpenseLines();
    const currencyPrecision = useCurrencyPrecision(fieldValues.currency);

    return (li: NetSuiteExpenseLineExtendedExpenseReport, value: number | null) => {
        if (!numberHelpers.isNumber(value)) {
            setOneExpenseLine(li.id, {
                amount: null,
                grossAmount: null,
                foreignAmount: null,
            });

            return;
        }

        const lineCurrencyPrecision = getLineCurrencyPrecision(li, currencies);
        const grossAmount = setCurrencyPrecision(value, currencyPrecision);
        const rate = (li.taxRate ?? 0) / 100;
        const amount = setCurrencyPrecision(grossAmount / (1 + rate), currencyPrecision);
        const taxAmount = setCurrencyPrecision(grossAmount - amount, currencyPrecision);
        const foreignAmount = li.exchangeRate
            ? setCurrencyPrecision(amount / li.exchangeRate, lineCurrencyPrecision)
            : li.foreignAmount;

        setOneExpenseLine(li.id, {
            amount,
            taxAmount,
            grossAmount,
            foreignAmount,
        });
    };
};

export const useUploadAttachment = () => {
    const dispatch = useDispatch();
    const { id, attachments } = useSelector(requestSelector);
    const expenseLines = useAtomValue(netSuiteExpenseLinesState);
    const { setOneExpenseLine } = useExpenseReportExpenseLines();
    const { onUploadAttachment: onUpload } = useExpenseLinesUploadAttachments();

    return useCallback(
        async (liId: string, files: File[]) => {
            const filesWithRequestUuid = files.map((file) => {
                return new File([file], getFileNameWithRequestId(file.name, id), {
                    type: file.type,
                    lastModified: file.lastModified,
                });
            });

            const file = filesWithRequestUuid[0];

            const allAttachments = expenseLines.reduce(
                (attachments, expenseLine) => {
                    if (expenseLine.attachment) {
                        attachments.push(expenseLine.attachment);
                    }

                    return attachments;
                },
                [...attachments]
            );

            const isFileUploadedToRequest = allAttachments.some(({ name }) => file.name === name);

            if (isFileUploadedToRequest) {
                dispatch(
                    actions.addErrorToast(
                        messages.uploadErrorDuplicateName({
                            fileName: file.name,
                        })
                    )
                );

                return;
            }

            const attachmentInProgress: domain.RequestAttachment = {
                id: file.name,
                name: file.name,
                size: file.size,
                createdDate: Date.now().toString(),
                attachmentType: domain.RequestAttachmentType.General,
            };

            setOneExpenseLine(liId, { attachment: attachmentInProgress });

            const attachment = await onUpload(liId, filesWithRequestUuid);

            setOneExpenseLine(liId, { attachment: attachment || undefined });
        },
        [attachments, dispatch, expenseLines, id, onUpload, setOneExpenseLine]
    );
};

const useChangeCustomField = (availableCustomFields: domain.Field[]) => {
    const { updateCustomField } = useExpenseReportExpenseLines();

    return <FieldName extends 'customFields', RestPath extends string[]>(
        fieldName: FieldName,
        line: Line,
        restPath: RestPath
    ) => {
        const customFieldId = String(restPath[0]);
        const customFieldType = restPath[1] as CustomFieldValueKey & keyof typeof customRecordFieldNames;
        const customField = availableCustomFields.find((field) => field.id === customFieldId);
        const lineCustomField = line[fieldName][customFieldId];

        // If it's update of item of selected field, we don't need to update the custom field
        if (
            !customField ||
            customFieldType === customRecordFieldNames.valueRef ||
            customFieldType === customRecordFieldNames.valueRefs
        ) {
            return;
        }

        switch (customFieldType) {
            case customRecordFieldNames.valueRef:
                break;
            case customRecordFieldNames.valueRefId: {
                updateCustomField(
                    line.id,
                    customField,
                    customRecordFieldNames.valueRef,
                    lineCustomField[customRecordFieldNames.valueRef]
                );
                break;
            }

            case customRecordFieldNames.valueRefs:
                break;
            case customRecordFieldNames.valueRefsIds: {
                updateCustomField(
                    line.id,
                    customField,
                    customRecordFieldNames.valueRefs,
                    lineCustomField[customRecordFieldNames.valueRefs]
                );
                break;
            }

            default: {
                updateCustomField(line.id, customField, customFieldType, lineCustomField[customFieldType]);
            }
        }
    };
};

export const useColumnsDefinitionData = () => {
    const fieldValues = useAtomValue(netSuiteExpenseReportFieldValuesState);
    const { documentFields, accounts, currencies, departments, classes, locations } = useSelector(getNetSuiteContext);
    const fieldsSettings = useNetSuiteExpenseReportExpenseLinesFieldSettings(documentFields);
    const currencyPrecision = useCurrencyPrecision(fieldValues.currency);
    const attachmentUploadingLineIds = useAtomValue(netSuiteAttachmentUploadingLineIdsState);
    const { onPreviewAttachment } = useNetSuiteExpenseLinesPreviewAttachment();

    return useMemo(() => {
        return {
            fieldValues,
            accounts,
            currencies,
            departments,
            classes,
            locations,
            fieldsSettings,
            currencyPrecision,
            attachmentUploadingLineIds,
            onPreviewAttachment,
        };
    }, [
        fieldValues,
        accounts,
        currencies,
        departments,
        classes,
        locations,
        fieldsSettings,
        currencyPrecision,
        attachmentUploadingLineIds,
        onPreviewAttachment,
    ]);
};

export const useCustomFieldsColumnDefinitionData = () => {
    const { template } = useSelector(requestSelector);
    const { customFields } = useSelector(getNetSuiteContext);
    const availableCustomFields = useAvailableCustomFields({ customFields, workflowId: template.id });

    return useMemo(() => {
        return {
            availableCustomFields,
            sublistType: domain.DocumentSublistType.ExpenseList,
            namePrefix: linesNamePrefix,
            workflowId: template.id,
        };
    }, [availableCustomFields, template.id]);
};

export const useColumnsDefinition = () => {
    const columnsDefinitionData = useColumnsDefinitionData();
    const customFieldsColumnDefinitionData = useCustomFieldsColumnDefinitionData();

    return useMemo(
        () => [
            ...getColumnsDefinition(columnsDefinitionData),
            ...(getCustomFieldsColumnDefinition(
                customFieldsColumnDefinitionData
                // I don't know why it works in old version without this type cast
            ) as unknown as ColumnDefinitionForExpenseReportExtended[]),
        ],
        [columnsDefinitionData, customFieldsColumnDefinitionData]
    );
};

export const useCheckedItems = () => {
    const request = useSelector(requestSelector);
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const isApproverMode = editMode === RequestEditMode.Approver;
    const expenseLines = useAtomValue(netSuiteExpenseLinesState);
    const { setOneExpenseLine } = useExpenseReportExpenseLines();

    const checkedItems = useMemo(() => {
        if (isApproverMode) return [];

        return expenseLines.reduce<string[]>((acc, line) => {
            if (line.checked) acc.push(line.id);

            return acc;
        }, []);
    }, [expenseLines, isApproverMode]);

    const handleCheckedItemsChange = useCallback(
        (checkedIds: string[]) => {
            expenseLines.forEach((line) => {
                setOneExpenseLine(line.id, { checked: checkedIds.includes(line.id) });
            });
        },
        [expenseLines, setOneExpenseLine]
    );

    return {
        checkedItems,
        handleCheckedItemsChange,
    };
};

export const useGroupManagement = () => {
    const expenseLines = useAtomValue(netSuiteExpenseLinesState);
    const { template } = useSelector(requestSelector);
    const { accounts, currencies, departments, classes, locations, documentFields } = useSelector(getNetSuiteContext);
    const fieldsSettings = useNetSuiteExpenseReportExpenseLinesFieldSettings(documentFields);
    const availableFields = getGroupEditItemsAvailableFields(fieldsSettings);
    const { availableCustomFields } = useCustomFieldsColumnDefinitionData();
    const { updateCustomField, onCloneLine, onRemoveLine, onFieldUpdate, onMultiLineFieldUpdate } =
        useExpenseReportExpenseLines();
    const onChangeCurrency = useChangeCurrency();
    const onChangeCategory = useChangeCategory();
    const onChangeTaxCode = useChangeTaxCode();

    return useGroupEditItems({
        workflowId: template.id,
        accounts,
        currencies,
        departments,
        classes,
        locations,
        availableFields,
        availableCustomFields,
        items: expenseLines,
        onCloneLine,
        onRemoveLine,
        onChangeCurrency,
        onChangeExpenseCategory: onChangeCategory,
        onChangeTaxCode,
        updateCustomField,
        onFieldUpdate,
        onMultiLineFieldUpdate,
    });
};

export const useActionMenuItems = <Item extends NetSuiteExpenseLineExtendedExpenseReport>() => {
    const { onCloneLine, onRemoveLine } = useExpenseReportExpenseLines();

    return useMemo(() => {
        return [
            {
                label: messages.cloneLine,
                onClick: ({ id }: Item) => onCloneLine(id),
            },
            {
                label: messages.deleteLine,
                onClick: ({ id }: Item) => onRemoveLine(id),
            },
        ];
    }, [onCloneLine, onRemoveLine]);
};

export const useTableData = () => {
    const expenseLines = useAtomValue(netSuiteExpenseLinesState);

    return useMemo(() => {
        return expenseLines.map((field, index) => ({
            ...field,
            index,
        }));
    }, [expenseLines]);
};

export const useFormData = () => {
    const expenseLines = useAtomValue(netSuiteExpenseLinesState);

    return useMemo(() => {
        return expenseLines.map((line) => ({
            ...line,
            attachment: line.attachment ? [line.attachment] : [],
            categoryId: line.category?.id ?? null,
            classId: line.class?.id ?? null,
            currencyId: line.currency?.id ?? null,
            customerId: line.customer?.id ?? null,
            departmentId: line.department?.id ?? null,
            expenseAccountId: line.expenseAccount?.id ?? null,
            locationId: line.location?.id ?? null,
            taxCodeId: line.taxCode?.id ?? null,
            customFields: (line.customFields ?? []).reduce<Line['customFields']>((acc, customField) => {
                acc[customField.companyFieldId] = {
                    ...customField,
                    [customRecordFieldNames.valueRefId]: customField[customRecordFieldNames.valueRef]?.id,
                    [customRecordFieldNames.valueRefsIds]: customField[customRecordFieldNames.valueRefs]?.map(
                        (ref) => ref.id
                    ),
                };

                return acc;
            }, {}),
        }));
    }, [expenseLines]);
};

export const useExpenseForm = <LineData extends NetSuiteExpenseLineExtendedExpenseReport>(tableData: LineData[]) => {
    const { template } = useSelector(requestSelector);
    const { documentFields } = useSelector(getNetSuiteContext);
    const { availableCustomFields } = useCustomFieldsColumnDefinitionData();
    const fieldsSettings = useNetSuiteExpenseReportExpenseLinesFieldSettings(documentFields);
    const showErrors = useAtomValue(netSuiteShowErrorsState);
    const formData = useFormData();

    const { onFieldUpdate } = useExpenseReportExpenseLines();
    const onChangeForeignAmount = useChangeForeignAmount();
    const onChangeCurrency = useChangeCurrency();
    const onChangeExchangeRate = useChangeExchangeRate();
    const onChangeAmount = useChangeAmount();
    const onChangeTaxCode = useChangeTaxCode();
    const onChangeGrossAmount = useChangeGrossAmount();
    const onUploadAttachment = useUploadAttachment();
    const onChangeCategory = useChangeCategory();
    const onChangeCustomField = useChangeCustomField(availableCustomFields);

    const validationSchema = useMemo(
        () => createSchema({ fieldsSettings, availableCustomFields, workflowId: template.id }),
        [fieldsSettings, availableCustomFields, template.id]
    );

    const form = useForm<FormValues>({
        values: { [linesNamePrefix]: formData },
        mode: 'onChange',
        resetOptions: {
            keepErrors: true,
        },
        resolver: zodResolver(validationSchema),
    });

    const handleFormChange: ExtractComponentProp<typeof Form<FormValues>, 'onChange'> = async (
        values,
        rootName,
        path
    ) => {
        const [_rootName, lineIndex, fieldName, ...restPath] = path;

        if (!lineIndex || !fieldName) return;

        const line = values[rootName][lineIndex];

        switch (fieldName) {
            case 'category':
                break;
            case 'categoryId': {
                onChangeCategory(tableData[lineIndex], line.category);
                break;
            }

            case 'expenseAccount':
                break;
            case 'expenseAccountId': {
                onFieldUpdate(line.id, 'expenseAccount', line.expenseAccount);
                break;
            }

            case 'foreignAmount': {
                onChangeForeignAmount(tableData[lineIndex], line[fieldName]);
                break;
            }

            case 'currency':
                break;
            case 'currencyId': {
                onChangeCurrency(tableData[lineIndex], line.currency);

                // Setting the currency also sets the exchange rate, so we need to revalidate the exchange rate field
                setTimeout(() => {
                    form.trigger(`${rootName}.${lineIndex}.exchangeRate`);
                }, 0);
                break;
            }

            case 'exchangeRate': {
                onChangeExchangeRate(tableData[lineIndex], line[fieldName]);
                break;
            }

            case 'amount': {
                onChangeAmount(tableData[lineIndex], line[fieldName]);
                break;
            }

            case 'taxCode':
                break;
            case 'taxCodeId': {
                onChangeTaxCode(tableData[lineIndex], line.taxCode);
                break;
            }

            case 'grossAmount': {
                onChangeGrossAmount(tableData[lineIndex], line[fieldName]);
                break;
            }

            case 'department':
                break;
            case 'departmentId': {
                onFieldUpdate(line.id, 'department', line.department);
                break;
            }

            case 'class':
                break;
            case 'classId': {
                onFieldUpdate(line.id, 'class', line.class);
                break;
            }

            case 'location':
                break;
            case 'locationId': {
                onFieldUpdate(line.id, 'location', line.location);
                break;
            }

            case 'customer':
                break;
            case 'customerId': {
                onFieldUpdate(line.id, 'customer', line.customer);

                form.setValue(`${rootName}.${lineIndex}.isBillable`, Boolean(line.customer && line.isBillable));
                break;
            }

            case 'attachment': {
                if (line.attachment[0] && 'source' in line.attachment[0]) {
                    onUploadAttachment(line.id, [line.attachment[0].source]);
                } else {
                    onFieldUpdate(line.id, 'attachment', null);
                }

                break;
            }

            case 'customFields': {
                onChangeCustomField(fieldName, line, restPath);

                break;
            }

            default: {
                onFieldUpdate(line.id, fieldName, line[fieldName]);
            }
        }
    };

    // If request is not valid, trigger form validation
    useEffect(() => {
        if (showErrors) {
            form.trigger();
        }
    }, [showErrors, form]);

    return { form, handleFormChange };
};
